import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { STOREFRONT_PREVIEW_KEY } from '@/composables/storefrontPreviewContext';
import es from '@/locales/es';
import en from '@/locales/en';

/**
 * Capa i18n liviana SIN vue-i18n (sin dependencia nueva).
 *
 * - `locale` es el estado reactivo en el store vuex (persistido en localStorage
 *   bajo la clave `godea_locale`, ver store/index.js).
 * - `t(key, params)` resuelve contra el catálogo activo; si la key falta en el
 *   idioma seleccionado, cae al valor `es` (fuente de verdad); si tampoco está
 *   en `es`, devuelve la propia key (nunca vacío).
 * - `{placeholder}` en el template se interpola con `params`.
 */

export const SUPPORTED_LOCALES = ['es', 'en'];
export const DEFAULT_LOCALE = 'es';
export const LOCALE_STORAGE_KEY = 'godea_locale';

const catalogs = { es, en };

/** Normaliza un valor locale a es/en (whitelist). */
export function resolveLocale(value) {
  const normalized = String(value || '').toLowerCase().trim();
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
}

function interpolate(template, params) {
  if (!params || typeof params !== 'object') return template;
  return String(template).replace(/\{(\w+)\}/g, (match, key) => {
    const val = params[key];
    return val === undefined || val === null ? match : String(val);
  });
}

/**
 * Resolución pura (útil fuera de componentes, p.ej. en services):
 * `translate(locale, key, params)`.
 */
export function translate(locale, key, params) {
  const active = resolveLocale(locale);
  const catalog = catalogs[active] || catalogs[DEFAULT_LOCALE];
  const esValue = catalogs.es ? catalogs.es[key] : undefined;
  let value = catalog ? catalog[key] : undefined;
  if (value === undefined && esValue !== undefined) value = esValue;
  if (value === undefined || value === '') value = key;
  return interpolate(value, params);
}

/**
 * Composable a usar dentro de componentes Vue:
 * `const { locale, t, setLocale } = useI18n()`.
 *
 * La reactividad del cambio de idioma funciona porque `t` lee `locale.value`
 * durante el render (trackeado por Vue) y `setLocale` persiste + commitea el
 * nuevo valor en el store.
 *
 * Opcional: `useI18n({ fixedLocale: 'es' })` congela el idioma en el catálogo
 * indicado (el storefront puede seguir alternando idioma sin afectar a este
 * componente). Se usa en el panel de administración: el admin siempre se
 * muestra en español, sin importar el idioma seleccionado en la web pública.
 */
export function useI18n(options = {}) {
  const store = useStore();
  const preview = inject(STOREFRONT_PREVIEW_KEY, null);

  const fixedLocale = options.fixedLocale ? resolveLocale(options.fixedLocale) : null;

  const locale = computed(() => fixedLocale || preview?.locale.value || store.getters.currentLocale || DEFAULT_LOCALE);

  const t = (key, params) => translate(fixedLocale || locale.value, key, params);

  const setLocale = (lang) => preview ? undefined : store.dispatch('setLocale', lang);

  return { locale, t, setLocale };
}

export default useI18n;
